import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Button,
  Collapse,
} from '@mui/material';
import {
  Home as HomeIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../../utils/config';

interface AuditLog {
  id: string;
  user: {
    id: string;
    email: string;
    name?: string;
  };
  entityType: string;
  entityId: string;
  action: string;
  oldValue?: any;
  newValue?: any;
  notes?: string;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

interface Filters {
  userId?: string;
  entityType?: string;
  entityId?: string;
  action?: string;
  dateFrom?: string;
  dateTo?: string;
}

const ENTITY_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'bill', label: 'Bill' },
];

const ACTION_TYPES = [
  { value: '', label: 'All Actions' },
  { value: 'view', label: 'View' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
  { value: 'approve', label: 'Approve' },
  { value: 'reject', label: 'Reject' },
  { value: 'associate', label: 'Associate' },
  { value: 'disassociate', label: 'Dissociate' },
  { value: 'export', label: 'Export' },
  { value: 'email', label: 'Email' },
  { value: 'print', label: 'Print' },
];

export const AuditLogsPage: React.FC = () => {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Filters>({});
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const isFetchingRef = useRef(false);
  
  const fetchAuditLogs = useCallback(async () => {
    // Prevent concurrent requests
    if (isFetchingRef.current) {
      return;
    }
    
    isFetchingRef.current = true;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        offset: (page * rowsPerPage).toString(),
        limit: rowsPerPage.toString(),
      });
      
      // Add filters to params
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.entityType) params.append('entityType', filters.entityType);
      if (filters.entityId) params.append('entityId', filters.entityId);
      if (filters.action) params.append('action', filters.action);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      
      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/audits?${params.toString()}`
      );
      
      if (!response || !response.ok) {
        throw new Error('Failed to fetch audit logs');
      }
      
      const data = await response.json();
      setAuditLogs(data.data?.audits || []);
      setTotalCount(data.data?.pagination?.total || 0);
      setError(null);
    } catch (err: any) {
      // Don't set error if it's an abort error
      if (err?.name !== 'AbortError') {
        console.error('Error fetching audit logs:', err);
        setError('Failed to fetch audit logs');
      }
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [page, rowsPerPage, filters, authenticatedFetch]);
  
  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);
  
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleFilterChange = (field: keyof Filters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value || undefined
    }));
    setPage(0); // Reset to first page when filters change
  };
  
  const handleResetFilters = () => {
    setFilters({});
    setPage(0);
  };
  
  const toggleRowExpanded = (id: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };
  
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };
  
  const getActionColor = (action: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
    switch (action.toLowerCase()) {
      case 'create':
      case 'approve':
        return 'success';
      case 'update':
      case 'associate':
      case 'disassociate':
        return 'warning';
      case 'delete':
      case 'reject':
        return 'error';
      case 'view':
      case 'export':
      case 'email':
      case 'print':
        return 'info';
      default:
        return 'default';
    }
  };
  
  const getEntityTypeColor = (entityType: string): 'primary' | 'secondary' | 'default' => {
    switch (entityType.toLowerCase()) {
      case 'vendor':
        return 'primary';
      case 'invoice':
        return 'secondary';
      case 'bill':
        return 'default';
      default:
        return 'default';
    }
  };
  
  const hasActiveFilters = Object.values(filters).some(value => value);
  
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/admin/hub')}
        >
          Admin Hub
        </Link>
        <Typography color="text.primary">Audit Logs</Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <HistoryIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Audit Logs
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          View all accounting-related user activity and changes.
        </Typography>
      </Box>
      
      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Actions Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            startIcon={showFilters ? <ExpandLessIcon /> : <FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            variant={hasActiveFilters ? 'contained' : 'outlined'}
            size="small"
          >
            Filters {hasActiveFilters && '(Active)'}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton onClick={fetchAuditLogs} title="Refresh" disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Stack>
        
        {/* Expandable Filters Section */}
        <Collapse in={showFilters}>
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField
                  size="small"
                  label="User ID or Email"
                  value={filters.userId || ''}
                  onChange={(e) => handleFilterChange('userId', e.target.value)}
                  sx={{ minWidth: 200 }}
                  disabled={loading}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Entity Type</InputLabel>
                  <Select
                    value={filters.entityType || ''}
                    onChange={(e: SelectChangeEvent) => handleFilterChange('entityType', e.target.value)}
                    label="Entity Type"
                    disabled={loading}
                  >
                    {ENTITY_TYPES.map(type => (
                      <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Entity ID"
                  value={filters.entityId || ''}
                  onChange={(e) => handleFilterChange('entityId', e.target.value)}
                  sx={{ minWidth: 150 }}
                  disabled={loading}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Action</InputLabel>
                  <Select
                    value={filters.action || ''}
                    onChange={(e: SelectChangeEvent) => handleFilterChange('action', e.target.value)}
                    label="Action"
                    disabled={loading}
                  >
                    {ACTION_TYPES.map(action => (
                      <MenuItem key={action.value} value={action.value}>{action.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField
                  size="small"
                  label="Date From"
                  type="datetime-local"
                  value={filters.dateFrom || ''}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 200 }}
                  disabled={loading}
                />
                <TextField
                  size="small"
                  label="Date To"
                  type="datetime-local"
                  value={filters.dateTo || ''}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 200 }}
                  disabled={loading}
                />
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleResetFilters}
                  disabled={!hasActiveFilters || loading}
                >
                  Reset Filters
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Collapse>
      </Paper>
      
      {/* Audit Logs Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : auditLogs.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>
            No audit logs found.
          </Alert>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="40"></TableCell>
                  <TableCell>Date/Time</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Entity Type</TableCell>
                  <TableCell>Entity ID</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>IP Address</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLogs.map((log) => (
                  <React.Fragment key={log.id}>
                    <TableRow hover>
                      <TableCell>
                        {(log.oldValue || log.newValue) && (
                          <IconButton
                            size="small"
                            onClick={() => toggleRowExpanded(log.id)}
                          >
                            {expandedRows.has(log.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(log.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PersonIcon fontSize="small" color="action" />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {log.user.name || 'Unknown'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {log.user.email}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={log.entityType} 
                          size="small"
                          color={getEntityTypeColor(log.entityType)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {log.entityId || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={log.action} 
                          size="small"
                          color={getActionColor(log.action)}
                        />
                      </TableCell>
                      <TableCell>
                        {log.notes ? (
                          <Tooltip title={log.notes}>
                            <Typography variant="body2" sx={{ cursor: 'help', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {log.notes}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {log.ipAddress || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {/* Expanded Row for Value Changes */}
                    {(log.oldValue || log.newValue) && (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 0 }}>
                          <Collapse in={expandedRows.has(log.id)}>
                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Stack spacing={2}>
                                {log.oldValue && (
                                  <Box>
                                    <Typography variant="subtitle2" color="error" gutterBottom>
                                      Old Value:
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 1, bgcolor: 'background.paper' }}>
                                      <pre style={{ margin: 0, fontSize: '12px', overflow: 'auto' }}>
                                        {JSON.stringify(log.oldValue, null, 2)}
                                      </pre>
                                    </Paper>
                                  </Box>
                                )}
                                {log.newValue && (
                                  <Box>
                                    <Typography variant="subtitle2" color="success.main" gutterBottom>
                                      New Value:
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 1, bgcolor: 'background.paper' }}>
                                      <pre style={{ margin: 0, fontSize: '12px', overflow: 'auto' }}>
                                        {JSON.stringify(log.newValue, null, 2)}
                                      </pre>
                                    </Paper>
                                  </Box>
                                )}
                                {log.userAgent && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      User Agent: {log.userAgent}
                                    </Typography>
                                  </Box>
                                )}
                              </Stack>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </TableContainer>
    </Container>
  );
};